import { palette } from './palette';
/**
 * AbbisWay color tokens for **web** (and the shared/default module).
 * Clinical blue/grey shell — unchanged while the warm mobile theme ships native-first.
 * Native overrides live in `paperThemeColors.native.ts` (Metro platform resolve).
 * Primitives stay in `palette.ts`; only `src/theme/` should import `palette` directly.
 */
export const paperAppColors = {
    // —— Abbi Theme semantic tokens (sheet names) ——
    // Native maps these to the token sheet. Web maps them to the clinical colors the
    // shared components already used here, so the web admin look does not change.
    card: palette.neutral0,
    tint: palette.neutral70,
    hairline: palette.neutral90,
    accent: palette.linkIos,
    accentPressed: palette.linkVariant,
    onAccent: palette.neutral0,
    action: palette.brand500,
    actionPressed: palette.brand400,
    onAction: palette.neutral0,
    link: palette.linkIos,
    rowChevron: palette.amber700,
    phaseLossFill: palette.phaseLossBg,
    phaseLossText: palette.phaseLossFg,
    phaseMaintFill: palette.phaseMaintBg,
    phaseMaintText: palette.phaseMaintFg,
    phaseRewardFill: palette.phaseRewardBg,
    phaseRewardText: palette.phaseRewardFg,
    // Native-only Home banner; web has no banner, keys kept for the shared type.
    encouragementFill: palette.green55,
    encouragementText: palette.green700,
    encouragementIcon: palette.green600,
    dangerFill: palette.red50,
    focusRing: palette.focusRingWeb,
    tipFill: palette.amber50,
    tipText: palette.amber900,
    tipIcon: palette.amber700,
    // —— App shell & surfaces ——
    backgroundScreen: palette.neutral50,
    backgroundScreenAlt: palette.neutral45,
    backgroundScreenSoft: palette.neutral48,
    backgroundKeyboardAccessory: palette.neutral48,
    backgroundSignInWeb: palette.neutral49,
    surfaceElevated: palette.neutral0,
    surfaceElevatedMuted: palette.neutral35,
    /** Home menu cards (warm mobile theme); web keeps plain elevated white. */
    surfaceMenuCard: palette.neutral0,
    /** Soft disc behind hub / header icons. */
    surfaceIconWell: palette.neutral70,
    surfaceTableHeader: palette.neutral35,
    surfaceTableAlt: palette.neutral35,
    surfaceBlueTint: palette.neutral55,
    surfaceBlueTintStrong: palette.neutral60,
    surfaceCurrentWeek: palette.blue50,
    surfaceWarningSoft: palette.amber45,
    surfaceWarningMuted: palette.amber55,
    surfaceDangerSoft: palette.red50,
    surfaceSuccessSoft: palette.green55,
    surfaceKeyboardField: palette.neutral0,
    surfaceProgressTrack: palette.neutral80,
    surfaceSuggestionFallback: palette.neutral75,
    surfaceIconButton: palette.neutral70,
    borderDefault: palette.neutral95,
    borderSubtle: palette.neutral110,
    borderHairline: palette.neutral90,
    borderMuted: palette.neutral96,
    borderKeyboard: palette.neutral96,
    borderInfo: palette.blue100,
    borderInfoStrong: palette.blue110,
    borderWarm: palette.amber210,
    borderSuccess: palette.green100,
    borderImage: palette.neutral150,
    borderSoft: palette.neutral90,
    // —— Typography ——
    textPrimary: palette.neutral225,
    textSecondary: palette.neutral180,
    textMuted: palette.neutral150,
    textTertiary: palette.neutral160,
    textQuaternary: palette.neutral170,
    textEmphasis: palette.neutral230,
    textTitle: palette.neutral240,
    textBody: palette.neutral210,
    textSecondaryStrong: palette.neutral200,
    textHeading: palette.neutral235,
    textPrimaryStrong: palette.neutral250,
    textIosTertiary: palette.neutral176,
    textIosSecondary: palette.neutral175,
    textCaption: palette.neutral195,
    textInverse: palette.neutral0,
    textOnPrimary: palette.neutral0,
    textPlaceholder: palette.neutral195,
    textPlaceholderStrong: palette.neutral185,
    textLink: palette.linkIos,
    textLinkBrand: palette.brand500,
    textLinkStrong: palette.linkStrong,
    textLinkBright: palette.linkBright,
    textLinkSecondary: palette.linkSecondary,
    textLinkVariant: palette.linkVariant,
    textKeyboardLabel: palette.neutral220,
    textSubtleOnInverse: palette.scrimLight70,
    // —— Actions ——
    actionPrimary: palette.brand500,
    actionPrimaryAlt: palette.linkIos,
    actionSecondary: palette.linkIos,
    controlInactive: palette.neutral95,
    controlDisabled: palette.neutral120,
    controlDisabledStrong: palette.neutral130,
    controlNeutral: palette.neutral600BlueGrey,
    buttonDisabledBackground: palette.buttonDisabledBg,
    actionDestructive: palette.red600,
    actionSuccessFill: palette.green650,
    // —— Status ——
    danger: palette.red700,
    dangerStrong: palette.red900,
    dangerText: palette.red600,
    dangerBackground: palette.red50,
    success: palette.bootstrapSuccess,
    successStrong: palette.green650,
    successText: palette.green700,
    successDeep: palette.green750,
    successTextMedium: palette.green600,
    successBackground: palette.green55,
    successTint: palette.green55,
    // —— Informational (saved state, portion hint) ——
    surfaceInfo: palette.blue50,
    infoText: palette.blue600,
    infoTextEmphasis: palette.blue900,
    infoTextOnTint: palette.neutral245,
    // —— Tips ——
    tipsBackground: palette.amber50,
    tipsBorder: palette.amber200,
    tipsTitle: palette.amber700,
    tipsBody: palette.amber900,
    // —— Meal validation gate (amber) ——
    validationGateBackground: palette.amber40,
    validationGateBorder: palette.amber210,
    validationGateTitle: palette.amber950,
    validationGateBody: palette.amber950,
    validationGateDot: palette.amber950,
    // —— Meal entry chips / links ——
    validationChipBackground: palette.blue50,
    validationChipBorder: palette.blue90,
    validationChipText: palette.linkBright,
    validationFixBackground: palette.green55,
    validationFixText: palette.green850,
    validationLink: palette.linkBright,
    secondaryCta: palette.linkSecondary,
    pillText: palette.neutral250,
    // —— Portion clarity toast ——
    portionToastBackground: palette.blue50,
    portionToastBorder: palette.blue110,
    portionToastText: palette.neutral245,
    // —— Charts & data viz ——
    chartPositive: palette.green400,
    chartWarning: palette.yellow600,
    chartNegative: palette.red400,
    chartWater: palette.cyan500,
    chartExercise: palette.chartOrange,
    chartLoss: palette.linkIos,
    chartMaintenance: palette.amber750,
    chartReward: palette.purple600,
    chartTrendUp: palette.green600,
    chartTrendDown: palette.red600,
    chartTrendNeutral: palette.neutral180,
    chartEmpty: palette.neutral125,
    // —— Admin / meal map ——
    adminRefreshPrimary: palette.blue700,
    adminRefreshSecondary: palette.blue750,
    adminOffPlanBorder: palette.red400,
    adminOffPlanBackground: palette.amber45,
    adminConfidenceHigh: palette.green400,
    adminConfidenceMid: palette.yellow600,
    adminConfidenceLow: palette.red400,
    adminTextOnType: palette.neutral230,
    adminRewardWarning: palette.amber800,
    // —— Weigh-in keypad ——
    keypadAppleGreen: palette.green500,
    keypadAppleBlue: palette.linkIos,
    keypadAppleRed: palette.red500,
    keypadAppleIndigo: palette.iosIndigo,
    // —— Program type (loss / maintenance / reward) ——
    programPhaseLossBackground: palette.phaseLossBg,
    programPhaseMaintenanceBackground: palette.phaseMaintBg,
    programPhaseRewardBackground: palette.phaseRewardBg,
    programPhaseLossForeground: palette.phaseLossFg,
    programPhaseMaintenanceForeground: palette.phaseMaintFg,
    programPhaseRewardForeground: palette.phaseRewardFg,
    // —— Program phase pill fallback (unknown type) ——
    phasePillFallbackBackground: palette.neutral80,
    // —— Side panel ——
    sidePanelBackground: palette.sidePanelBg,
    // —— Shadows (native) ——
    shadow: palette.shadowBlack,
    /** RGB channels only, for `rgba(...)` in web `boxShadow` templates */
    shadowRgbChannel: palette.blackRgbChannel,
    /** Layout / hit targets */
    transparent: palette.transparent,
    // —— Overlays & scrims ——
    overlayModal: palette.overlayScrim50,
    overlayMenu: palette.overlayScrim30,
    overlayFab: palette.overlayScrim70,
    overlayImage: palette.overlayScrim50,
    surfaceUnderlayLight: palette.overlayScrim06,
    surfaceUnderlayMedium: palette.overlayScrim10,
    surfaceUnderlay04: palette.overlayScrim04,
    surfaceTranslucent: palette.surfaceTranslucent90,
    // —— Misc ——
    eyeIcon: palette.linkStrong,
    eyeIconWeb: palette.eyeWeb,
    eyeIconBorder: palette.neutral120,
    errorBanner: palette.dangerTranslucent80,
    generateProgramError: palette.redGenerate,
    signInEyeWeb: palette.eyeWeb,
    avatarBackground: palette.avatarBlue,
    // —— CRM state chips ——
    stateChipOnPlanBackground: palette.green55,
    stateChipRecoveryBackground: palette.red100,
    stateChipOnPlanText: palette.green800,
    // —— Photo picker ——
    photoItemAccent: palette.brand400,
    // —— Web nav arrow ——
    navArrowBackground: palette.navArrowBgDefault,
    navArrowBackgroundHover: palette.navArrowBgHover,
    navArrowBackgroundDisabled: palette.navArrowBgDisabled,
    navArrowForeground: palette.navArrowFgDefault,
    navArrowForegroundDisabled: palette.navArrowFgDisabled,
    controlFocusRing: palette.focusRingWeb,
};
